import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import MarkdownToolbar from '@/components/MarkdownToolbar';
import PreviewModal from '@/components/PreviewModal';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import remarkEmoji from 'remark-emoji';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { vscDarkPlus } from 'react-syntax-highlighter/dist/esm/styles/prism';
import EmojiPicker from 'emoji-picker-react';
import { ArrowLeft, Save, Eye, Code2 } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PostBuilder = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEditing = !!id;
  const textareaRef = useRef(null);

  const [loading, setLoading] = useState(false);
  const [title, setTitle] = useState('');
  const [excerpt, setExcerpt] = useState('');
  const [content, setContent] = useState('');
  const [featuredImage, setFeaturedImage] = useState('');
  const [status, setStatus] = useState('draft');
  const [tags, setTags] = useState([]);
  const [tagInput, setTagInput] = useState('');
  const [categories, setCategories] = useState([]);
  const [categoryInput, setCategoryInput] = useState('');
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [activeTab, setActiveTab] = useState('edit');
  const [showPreviewModal, setShowPreviewModal] = useState(false);

  useEffect(() => {
    if (isEditing) {
      fetchPost();
    }
  }, [id]);

  const fetchPost = async () => {
    try {
      const response = await axios.get(`${API}/posts/${id}`);
      const post = response.data;
      setTitle(post.title);
      setExcerpt(post.excerpt || '');
      setContent(post.content);
      setFeaturedImage(post.featured_image || '');
      setStatus(post.status);
      setTags(post.tags);
      setCategories(post.categories);
    } catch (error) {
      console.error('Error fetching post:', error);
      toast.error('Failed to load post');
    }
  };

  const insertMarkdown = (syntax) => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const selectedText = content.substring(start, end);
    
    let newText;
    if (syntax.includes('text')) {
      newText = syntax.replace('text', selectedText || 'text');
    } else if (syntax.includes('url')) {
      newText = syntax.replace('url', 'https://example.com');
    } else {
      newText = syntax;
    }

    const newContent = content.substring(0, start) + newText + content.substring(end);
    setContent(newContent);

    // Set cursor position
    setTimeout(() => {
      textarea.focus();
      const cursorPos = start + newText.length;
      textarea.setSelectionRange(cursorPos, cursorPos);
    }, 0);
  };

  const handleEmojiClick = (emojiData) => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    const start = textarea.selectionStart;
    const emoji = emojiData.emoji;
    const newContent = content.substring(0, start) + emoji + content.substring(start);
    setContent(newContent);
    setShowEmojiPicker(false);

    setTimeout(() => {
      textarea.focus();
      const cursorPos = start + emoji.length;
      textarea.setSelectionRange(cursorPos, cursorPos);
    }, 0);
  };

  const handleAddTag = () => {
    if (tagInput.trim() && !tags.includes(tagInput.trim())) {
      setTags([...tags, tagInput.trim()]);
      setTagInput('');
    }
  };

  const handleRemoveTag = (tagToRemove) => {
    setTags(tags.filter(tag => tag !== tagToRemove));
  };

  const handleAddCategory = () => {
    if (categoryInput.trim() && !categories.includes(categoryInput.trim())) {
      setCategories([...categories, categoryInput.trim()]);
      setCategoryInput('');
    }
  };

  const handleRemoveCategory = (categoryToRemove) => {
    setCategories(categories.filter(cat => cat !== categoryToRemove));
  };

  const handleSave = async (publishStatus) => {
    if (!title.trim()) {
      toast.error('Title is required');
      return;
    }
    if (!content.trim()) {
      toast.error('Content is required');
      return;
    }

    setLoading(true);

    const postData = {
      title,
      excerpt: excerpt || null,
      content,
      featured_image: featuredImage || null,
      status: publishStatus,
      tags,
      categories,
    };

    try {
      if (isEditing) {
        await axios.put(`${API}/posts/${id}`, postData);
        toast.success(publishStatus === 'published' ? 'Post published!' : 'Post updated!');
      } else {
        await axios.post(`${API}/posts`, postData);
        toast.success(publishStatus === 'published' ? 'Post published!' : 'Draft saved!');
      }
      navigate('/');
    } catch (error) {
      console.error('Error saving post:', error);
      toast.error('Failed to save post');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8" data-testid="post-builder">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-6 flex items-center justify-between">
          <Button
            variant="ghost"
            onClick={() => navigate('/')}
            className="flex items-center gap-2"
            data-testid="back-button"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Posts
          </Button>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              onClick={() => setShowPreviewModal(true)}
              data-testid="preview-button"
            >
              <Eye className="w-4 h-4 mr-2" />
              Preview
            </Button>
            <Button
              variant="outline"
              onClick={() => handleSave('draft')}
              disabled={loading}
              data-testid="save-draft-button"
            >
              <Save className="w-4 h-4 mr-2" />
              Save Draft
            </Button>
            <Button
              onClick={() => handleSave('published')}
              disabled={loading}
              className="bg-blue-600 hover:bg-blue-700"
              data-testid="publish-button"
            >
              {loading ? 'Saving...' : 'Publish'}
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Editor */}
          <div className="lg:col-span-2 space-y-4">
            {/* Title */}
            <Card className="p-4">
              <Input
                placeholder="Post Title"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                className="text-3xl font-bold border-none shadow-none px-0 focus-visible:ring-0"
                data-testid="title-input"
              />
            </Card>

            {/* Markdown Editor */}
            <Card className="overflow-hidden">
              <Tabs value={activeTab} onValueChange={setActiveTab}>
                <TabsList className="w-full justify-start rounded-none border-b">
                  <TabsTrigger value="edit" className="flex items-center gap-2">
                    <Code2 className="w-4 h-4" />
                    Edit
                  </TabsTrigger>
                  <TabsTrigger value="preview" className="flex items-center gap-2">
                    <Eye className="w-4 h-4" />
                    Preview
                  </TabsTrigger>
                </TabsList>

                <TabsContent value="edit" className="m-0">
                  <MarkdownToolbar
                    onInsert={insertMarkdown}
                    onEmojiClick={() => setShowEmojiPicker(!showEmojiPicker)}
                  />
                  {showEmojiPicker && (
                    <div className="absolute z-50 mt-2" data-testid="emoji-picker">
                      <EmojiPicker onEmojiClick={handleEmojiClick} />
                    </div>
                  )}
                  <Textarea
                    ref={textareaRef}
                    value={content}
                    onChange={(e) => setContent(e.target.value)}
                    placeholder="Write your post content in Markdown...\n\n# Heading 1\n## Heading 2\n\n**Bold text** and *italic text*\n\n- List item\n- Another item\n\n```javascript\nconst code = 'example';\n```\n\n![Image](https://example.com/image.jpg)"
                    className="min-h-[600px] border-none shadow-none resize-none font-mono text-sm focus-visible:ring-0 markdown-editor"
                    data-testid="content-input"
                  />
                </TabsContent>

                <TabsContent value="preview" className="m-0">
                  <div className="markdown-preview min-h-[600px]" data-testid="markdown-preview">
                    <ReactMarkdown
                      remarkPlugins={[remarkGfm, remarkEmoji]}
                      components={{
                        code({node, inline, className, children, ...props}) {
                          const match = /language-(\w+)/.exec(className || '');
                          return !inline && match ? (
                            <SyntaxHighlighter
                              style={vscDarkPlus}
                              language={match[1]}
                              PreTag="div"
                              {...props}
                            >
                              {String(children).replace(/\n$/, '')}
                            </SyntaxHighlighter>
                          ) : (
                            <code className={className} {...props}>
                              {children}
                            </code>
                          );
                        }
                      }}
                    >
                      {content || '*No content yet. Start writing in the Edit tab!*'}
                    </ReactMarkdown>
                  </div>
                </TabsContent>
              </Tabs>
            </Card>
          </div>

          {/* Sidebar */}
          <div className="space-y-4">
            {/* Excerpt */}
            <Card className="p-4">
              <Label className="text-sm font-medium mb-2 block">Excerpt</Label>
              <Textarea
                value={excerpt}
                onChange={(e) => setExcerpt(e.target.value)}
                placeholder="Brief summary of your post"
                rows={3}
                className="text-sm"
                data-testid="excerpt-input"
              />
            </Card>

            {/* Featured Image */}
            <Card className="p-4">
              <Label className="text-sm font-medium mb-2 block">Featured Image URL</Label>
              <Input
                value={featuredImage}
                onChange={(e) => setFeaturedImage(e.target.value)}
                placeholder="https://example.com/image.jpg"
                className="text-sm"
                data-testid="featured-image-input"
              />
              {featuredImage && (
                <div className="mt-3">
                  <img
                    src={featuredImage}
                    alt="Preview"
                    className="w-full h-32 object-cover rounded"
                    onError={(e) => e.target.style.display = 'none'}
                  />
                </div>
              )}
            </Card>

            {/* Tags */}
            <Card className="p-4">
              <Label className="text-sm font-medium mb-2 block">Tags</Label>
              <div className="flex gap-2 mb-3">
                <Input
                  value={tagInput}
                  onChange={(e) => setTagInput(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddTag())}
                  placeholder="Add tag"
                  className="text-sm"
                  data-testid="tag-input"
                />
                <Button type="button" onClick={handleAddTag} size="sm" data-testid="add-tag-button">
                  Add
                </Button>
              </div>
              <div className="flex flex-wrap gap-2">
                {tags.map((tag, i) => (
                  <Badge
                    key={i}
                    variant="secondary"
                    className="cursor-pointer hover:bg-red-100"
                    onClick={() => handleRemoveTag(tag)}
                    data-testid={`tag-${i}`}
                  >
                    {tag} ×
                  </Badge>
                ))}
              </div>
            </Card>

            {/* Categories */}
            <Card className="p-4">
              <Label className="text-sm font-medium mb-2 block">Categories</Label>
              <div className="flex gap-2 mb-3">
                <Input
                  value={categoryInput}
                  onChange={(e) => setCategoryInput(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddCategory())}
                  placeholder="Add category"
                  className="text-sm"
                  data-testid="category-input"
                />
                <Button type="button" onClick={handleAddCategory} size="sm" data-testid="add-category-button">
                  Add
                </Button>
              </div>
              <div className="flex flex-wrap gap-2">
                {categories.map((cat, i) => (
                  <Badge
                    key={i}
                    variant="secondary"
                    className="cursor-pointer hover:bg-red-100"
                    onClick={() => handleRemoveCategory(cat)}
                    data-testid={`category-${i}`}
                  >
                    {cat} ×
                  </Badge>
                ))}
              </div>
            </Card>
          </div>
        </div>

        {/* Preview Modal */}
        <PreviewModal
          open={showPreviewModal}
          onOpenChange={setShowPreviewModal}
          post={{
            title,
            content,
            excerpt,
            featured_image: featuredImage,
            tags,
            categories,
            status
          }}
        />
      </div>
    </div>
  );
};

export default PostBuilder;
